import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";

import {
  createClient,
  deleteClient,
  fetchClientDatagridRows,
  generateClientCid,
} from "../../services/clientService";
import { AppConfirmDialog, AppToast } from "../../components/AppFeedback";
import DatagridNavLink from "../../components/DatagridNavLink";
import PageBanner from "../../components/PageBanner";
import PageContent from "../../components/PageContent";
import { useUserContext } from "../../context/UserContext";
import { COUNTRIES } from "../../constants/countries";

const statusColorMap = {
  active: "success",
  completed: "info",
  pending: "warning",
  submitted: "default",
};

function toTitleCase(value = "") {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

const ClientList = () => {
  const navigate = useNavigate();
  const { roles = [] } = useUserContext();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: "client_name", sort: "asc" }]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteCid, setDeleteCid] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatingCid, setGeneratingCid] = useState(false);
  const [generatedCid, setGeneratedCid] = useState("");
  const [createForm, setCreateForm] = useState({
    cid: "",
    name: "",
    status: "pending",
    contact_first_name: "",
    contact_last_name: "",
    email: "",
    phone_local: "",
    address1: "",
    address2: "",
    country: "United States",
    city: "",
    state: "",
    zipcode: "",
  });
  const selectedCountry = useMemo(
    () => COUNTRIES.find((country) => country.name === createForm.country),
    [createForm.country]
  );
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const isAdmin = roles.some((role) => String(role).toUpperCase() === "ADMIN");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!createOpen) return;
    const name = createForm.name.trim();
    if (!name) {
      setGeneratedCid("");
      setCreateForm((prev) => ({ ...prev, cid: "" }));
      return;
    }

    const timer = setTimeout(async () => {
      setGeneratingCid(true);
      try {
        const data = await generateClientCid(name);
        const cid = data?.cid || "";
        setGeneratedCid(cid);
        setCreateForm((prev) => ({ ...prev, cid }));
      } catch (error) {
        setGeneratedCid("");
      } finally {
        setGeneratingCid(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [createForm.name, createOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const sort = sortModel[0] || {};
        const apiSortFieldMap = {
          client_id: "cid",
          client_name: "name",
          status: "status",
          contact_phone: "phone",
          username: "username",
          email: "email",
        };

        const response = await fetchClientDatagridRows({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          search: searchTerm,
          sortField: apiSortFieldMap[sort.field] || "cid",
          sortOrder: sort.sort || "asc",
        });

        if (cancelled) return;

        setRows(response.rows || []);
        setRowCount(response.rowCount || 0);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load clients");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [paginationModel, sortModel, searchTerm]);

  const handleDelete = async () => {
    if (!deleteCid) return;

    try {
      const sort = sortModel[0] || {};
      const apiSortFieldMap = {
        client_id: "cid",
        client_name: "name",
        status: "status",
        contact_phone: "phone",
        username: "username",
        email: "email",
      };

      await deleteClient(deleteCid);
      setSnackbar({
        open: true,
        message: "Client deleted successfully",
        severity: "success",
      });
      setDeleteCid(null);

      // Refresh current page after delete.
      const response = await fetchClientDatagridRows({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: searchTerm,
        sortField: apiSortFieldMap[sort.field] || "cid",
        sortOrder: sort.sort || "asc",
      });
      setRows(response.rows || []);
      setRowCount(response.rowCount || 0);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete client",
        severity: "error",
      });
      setDeleteCid(null);
    }
  };

  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateClient = async () => {
    if (!createForm.name.trim()) {
      setSnackbar({
        open: true,
        message: "Client name is required",
        severity: "warning",
      });
      return;
    }
    if (!createForm.contact_first_name.trim() || !createForm.contact_last_name.trim()) {
      setSnackbar({ open: true, message: "Contact first and last name are required", severity: "warning" });
      return;
    }
    if (!createForm.email.trim()) {
      setSnackbar({ open: true, message: "Email is required", severity: "warning" });
      return;
    }
    if (!(createForm.phone_local || "").trim()) {
      setSnackbar({ open: true, message: "Phone is required", severity: "warning" });
      return;
    }
    if (
      !createForm.address1.trim() ||
      !createForm.city.trim() ||
      !createForm.state.trim() ||
      !createForm.zipcode.trim() ||
      !createForm.country
    ) {
      setSnackbar({
        open: true,
        message: "Address is required (line 1, city, state, zip, country)",
        severity: "warning",
      });
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...createForm,
        phone: `${selectedCountry?.dialCode || ""}${(createForm.phone_local || "").trim()}`,
      };
      delete payload.phone_local;

      await createClient(payload);
      setCreateOpen(false);
      setCreateForm({
        cid: "",
        name: "",
        status: "pending",
        contact_first_name: "",
        contact_last_name: "",
        email: "",
        phone_local: "",
        address1: "",
        address2: "",
        country: "United States",
        city: "",
        state: "",
        zipcode: "",
      });
      setGeneratedCid("");
      setSnackbar({
        open: true,
        message: "Client created successfully",
        severity: "success",
      });

      const sort = sortModel[0] || {};
      const apiSortFieldMap = {
        client_id: "cid",
        client_name: "name",
        status: "status",
        contact_phone: "phone",
        username: "username",
        email: "email",
      };
      const response = await fetchClientDatagridRows({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: searchTerm,
        sortField: apiSortFieldMap[sort.field] || "cid",
        sortOrder: sort.sort || "asc",
      });
      setRows(response.rows || []);
      setRowCount(response.rowCount || 0);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to create client",
        severity: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "client_id",
        headerName: "Client ID",
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.client_id ? `/client-management/edit/${params.row.client_id}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "client_name",
        headerName: "Client Name",
        flex: 1.4,
        minWidth: 180,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.client_id ? `/client-management/edit/${params.row.client_id}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "contact_person",
        headerName: "Contact Person",
        flex: 1.2,
        minWidth: 160,
        sortable: false,
      },
      {
        field: "contact_phone",
        headerName: "Phone",
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      {
        field: "no_of_project",
        headerName: "Projects",
        type: "number",
        flex: 0.8,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.client_id ? `/client-management/edit/${params.row.client_id}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 130,
        renderCell: (params) => {
          const status = String(params.value || "").toLowerCase();
          return (
            <Chip
              label={toTitleCase(status)}
              color={statusColorMap[status] || "default"}
              size="small"
              variant="filled"
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/client-management/edit/${params.row.client_id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!isAdmin}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isAdmin) return;
                    setDeleteCid(params.row.client_id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [isAdmin, navigate]
  );

  return (
    <Box sx={{ bgcolor: "#dde7ecff", minHeight: "100vh" }}>
      <PageBanner title="Client" />

      <PageContent width={{ xs: "100%", sm: "95%", md: "85%", lg: "80%" }}>
        <Box sx={{ borderRadius: 1, p: { xs: 1.5, sm: 2 }, bgcolor: "#ffffff" }}>
          <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
            <TextField
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by client name"
              size="small"
              sx={{ width: { xs: "100%", sm: 320 } }}
            />
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
            }
            autoHeight
            sx={{
              "& .row-even": {
                backgroundColor: "#ffffff",
              },
              "& .row-odd": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </PageContent>

      <AppConfirmDialog
        open={Boolean(deleteCid)}
        title="Confirm Deletion"
        content={
          <>
            Are you sure you want to delete client <strong>{deleteCid}</strong>?
          </>
        }
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteCid(null)}
        onConfirm={handleDelete}
      />

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <Dialog
        open={createOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setCreateOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Create Client
          <IconButton
            aria-label="close"
            onClick={() => setCreateOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="cid"
                    label="CID (from backend)"
                    value={generatedCid}
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText={generatingCid ? "Generating CID..." : "Auto-generated by backend"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="name"
                    label="Client Name"
                    value={createForm.name}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    name="status"
                    label="Status"
                    value={createForm.status}
                    onChange={handleCreateChange}
                  >
                    {["pending", "active", "completed", "submitted"].map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.charAt(0).toUpperCase() + option.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="contact_first_name"
                    label="Contact First Name"
                    value={createForm.contact_first_name}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="contact_last_name"
                    label="Contact Last Name"
                    value={createForm.contact_last_name}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    name="email"
                    label="Manager Email"
                    value={createForm.email}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Country Code"
                    value={selectedCountry?.dialCode || ""}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="phone_local"
                    label="Mobile Number"
                    value={createForm.phone_local}
                    onChange={handleCreateChange}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Address
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="address1"
                    label="Address Line 1"
                    value={createForm.address1}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="address2"
                    label="Address Line 2"
                    value={createForm.address2}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    select
                    name="country"
                    label="Country"
                    value={createForm.country}
                    onChange={handleCreateChange}
                  >
                    {COUNTRIES.map((country) => (
                      <MenuItem key={country.code} value={country.name}>
                        {country.name} ({country.dialCode})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="city"
                    label="City"
                    value={createForm.city}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="state"
                    label="State"
                    value={createForm.state}
                    onChange={handleCreateChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    name="zipcode"
                    label="Zip Code"
                    value={createForm.zipcode}
                    onChange={handleCreateChange}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateClient} variant="contained" disabled={creating || !isAdmin}>
            {creating ? "Creating..." : "Create Client"}
          </Button>
        </DialogActions>
      </Dialog>

      <Tooltip
        title={
          <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
            Add New Client
          </Typography>
        }
      >
        <Fab
          color="primary"
          aria-label="add-client"
          onClick={() => {
            if (!isAdmin) {
              setSnackbar({
                open: true,
                message: "Only admin can create clients",
                severity: "warning",
              });
              return;
            }
            setCreateOpen(true);
          }}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 1000,
            bgcolor: "#2a366e",
            "&:hover": {
              backgroundColor: "#424d80",
            },
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default ClientList;
